import React, { useState, useEffect } from "react";
import { Plus, Save, List } from "lucide-react";
import {
  datamapsApi,
  clientsApi,
  datamapIndicatorsApi,
  indicatorsApi,
} from "../../utils/api";
import { FREQUENCIES, DELIVERY_METHODS } from "../../constants";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";
import DataMapIndicators from "./DataMapIndicators";

const DataMapManagement = () => {
  const [datamaps, setDatamaps] = useState([]);
  const [clients, setClients] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [datamapIndicators, setDatamapIndicators] = useState({});
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingDatamap, setEditingDatamap] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showIndicators, setShowIndicators] = useState(false);
  const [selectedDatamapId, setSelectedDatamapId] = useState(null);

  const [formData, setFormData] = useState({
    customerId: 0,
    frequency: "",
    active: true,
    prompt: "",
    name: "",
    deliveryMethod: "",
    destination: "",
    clientId: "",
  });

  useEffect(() => {
    fetchDatamaps();
    fetchClients();
    fetchIndicators();
  }, []);

  useEffect(() => {
    if (datamaps.length > 0) {
      fetchDatamapIndicatorAssociations();
    }
  }, [datamaps]);

  const fetchDatamaps = async () => {
    setLoading(true);
    try {
      const data = await datamapsApi.getAll();
      setDatamaps(data);
    } catch (error) {
      console.error("Error fetching datamaps:", error);
    }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchIndicators = async () => {
    try {
      const data = await indicatorsApi.getAll();
      setIndicators(data);
    } catch (error) {
      console.error("Error fetching indicators:", error);
    }
  };

  const fetchDatamapIndicatorAssociations = async () => {
    try {
      const associations = {};
      for (const datamap of datamaps) {
        const data = await datamapIndicatorsApi.getByDatamap(datamap.id);
        associations[datamap.id] = data || [];
      }
      setDatamapIndicators(associations);
    } catch (error) {
      console.error("Error fetching datamap indicator associations:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingDatamap) {
        await datamapsApi.update(editingDatamap.id, formData);
      } else {
        await datamapsApi.create(formData);
      }
      fetchDatamaps();
      resetForm();
    } catch (error) {
      console.error("Error saving datamap:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this datamap?"))
      return;
    setLoading(true);
    try {
      await datamapsApi.delete(id);
      fetchDatamaps();
    } catch (error) {
      console.error("Error deleting datamap:", error);
    }
    setLoading(false);
  };

  const handleEdit = (datamap) => {
    setEditingDatamap(datamap);
    setFormData({
      customerId: datamap.customerid || 0,
      frequency: datamap.frequency || "",
      active: datamap.active ?? true,
      prompt: datamap.prompt || "",
      name: datamap.name || "",
      deliveryMethod: datamap.deliverymethod || "",
      destination: datamap.destination || "",
      clientId: datamap.clientid || "",
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      customerId: 0,
      frequency: "",
      active: true,
      prompt: "",
      name: "",
      deliveryMethod: "",
      destination: "",
      clientId: "",
    });
    setEditingDatamap(null);
    setShowForm(false);
  };

  const filteredDatamaps = datamaps.filter(
    (datamap) =>
      datamap.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      datamap.destination?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleManageIndicators = (datamapId) => {
    setSelectedDatamapId(datamapId);
    setShowIndicators(true);
  };

  const getDatamapIndicatorNames = (datamapId) => {
    const associations = datamapIndicators[datamapId] || [];
    return associations
      .map((assoc) => {
        const indicator = indicators.find(
          (ind) => ind.id === (assoc.dataindicatorid || assoc.dataIndicatorID),
        );
        return indicator
          ? `${indicator.dataindicator} - ${indicator.source}`
          : null;
      })
      .filter(Boolean)
      .join(", ");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onRefresh={fetchDatamaps}
          placeholder="Search datamaps..."
        />
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          New DataMap
        </PrimaryButton>
      </div>

      {/* Form */}
      <FormModal
        title={editingDatamap ? "Edit DataMap" : "New DataMap"}
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Frequency *
              </label>
              <select
                required
                value={formData.frequency}
                onChange={(e) =>
                  setFormData({ ...formData, frequency: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select frequency</option>
                {FREQUENCIES.map((freq) => (
                  <option key={freq.value} value={freq.value}>
                    {freq.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Prompt
            </label>
            <textarea
              value={formData.prompt}
              onChange={(e) =>
                setFormData({ ...formData, prompt: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Delivery Method
              </label>
              <select
                value={formData.deliveryMethod}
                onChange={(e) =>
                  setFormData({ ...formData, deliveryMethod: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select method</option>
                {DELIVERY_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Destination
              </label>
              <input
                type="text"
                value={formData.destination}
                onChange={(e) =>
                  setFormData({ ...formData, destination: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Customer ID
              </label>
              <input
                type="number"
                value={formData.customerId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    customerId: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Client
              </label>
              <select
                value={formData.clientId}
                onChange={(e) =>
                  setFormData({ ...formData, clientId: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select client (optional)</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) =>
                  setFormData({ ...formData, active: e.target.checked })
                }
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-slate-700">
                Active
              </span>
            </label>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save DataMap"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Destination
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-slate-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filteredDatamaps.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-slate-500"
                  >
                    No datamaps found
                  </td>
                </tr>
              ) : (
                filteredDatamaps.map((datamap) => (
                  <tr key={datamap.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {datamap.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {datamap.frequency}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {datamap.deliverymethod}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {datamap.destination}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          datamap.active
                            ? "bg-green-100 text-green-800"
                            : "bg-slate-100 text-slate-800"
                        }`}
                      >
                        {datamap.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <ActionButtons
                          onEdit={() => handleEdit(datamap)}
                          onDelete={() => handleDelete(datamap.id)}
                        />
                        <button
                          onClick={() => handleManageIndicators(datamap.id)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Manage Indicators"
                        >
                          <List className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Data Indicators Modal */}
      <DataMapIndicators
        datamapId={selectedDatamapId}
        isOpen={showIndicators}
        onClose={() => setShowIndicators(false)}
      />
    </div>
  );
};

export default DataMapManagement;
